import { useState } from "react";
import axios from "axios";
import { 
  Upload, 
  Link2, 
  FileUp, 
  Clock, 
  CheckCircle2,
  RefreshCw,
  Database
} from "lucide-react";
import { API } from "@/utils/constants";

const UploadConfigurations = () => {
  const [selectedTab, setSelectedTab] = useState('file');
  const [dragActive, setDragActive] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [adpConnected, setAdpConnected] = useState(false);
  const [ukgConnected, setUkgConnected] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    setUploadStatus({ type: 'processing', message: `Processing ${file.name}...` });
    
    setTimeout(() => {
      setUploadStatus({ type: 'success', message: `Successfully uploaded ${file.name}. Configuration analysis will begin shortly.` });
    }, 2000);
  };

  const handleConnect = (system) => {
    if (system === 'adp') {
      setAdpConnected(true);
    } else if (system === 'ukg') {
      setUkgConnected(true);
    }
  };

  const handleRefreshCache = async () => {
    setRefreshing(true);
    setRefreshStatus(null);
    
    try {
      const response = await axios.post(`${API}/cache/refresh`);
      setRefreshStatus({ 
        type: 'success', 
        message: 'Cache cleared successfully! Pages will now load fresh data from the database.' 
      });
    } catch (error) {
      setRefreshStatus({ 
        type: 'error', 
        message: 'Failed to refresh cache. Please try again.' 
      });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div data-testid="upload-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white font-['Manrope']">Upload Configurations</h1>
        <p className="text-sm text-slate-400 mt-1">Import pay rule configurations from files or connect to your HR system</p>
      </div>

      {/* Database Refresh Section */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-[#2EC4B6]/20">
            <Database className="w-6 h-6 text-[#2EC4B6]" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-1">Data Cache Management</h3>
            <p className="text-sm text-slate-400 mb-4">
              Database results are cached for faster page loads. Click refresh to fetch the latest data from the database.
            </p>
            <button
              onClick={handleRefreshCache}
              disabled={refreshing}
              data-testid="refresh-cache-btn"
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                refreshing 
                  ? 'bg-slate-700 text-slate-400 cursor-not-allowed' 
                  : 'bg-[#2EC4B6] text-slate-900 hover:bg-[#26a69a] shadow-lg shadow-[#2EC4B6]/20'
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh from Database'}
            </button>
            
            {refreshStatus && (
              <div className={`mt-4 p-3 rounded-lg ${
                refreshStatus.type === 'success' 
                  ? 'bg-emerald-900/30 border border-emerald-700 text-emerald-300' 
                  : 'bg-red-900/30 border border-red-700 text-red-300'
              }`}>
                <div className="flex items-center gap-2">
                  {refreshStatus.type === 'success' && <CheckCircle2 className="w-4 h-4" />}
                  <span className="text-sm">{refreshStatus.message}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setSelectedTab('file')}
            data-testid="tab-file-upload"
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
              selectedTab === 'file'
                ? 'bg-slate-700/50 text-white border-b-2 border-[#2EC4B6]'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
            }`}
          >
            <FileUp className="w-5 h-5" />
            File Upload
          </button>
          <button
            onClick={() => setSelectedTab('adp')}
            data-testid="tab-adp"
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
              selectedTab === 'adp'
                ? 'bg-slate-700/50 text-white border-b-2 border-[#2EC4B6]'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
            }`}
          >
            <Link2 className="w-5 h-5" />
            ADP Integration
          </button>
          <button
            onClick={() => setSelectedTab('ukg')}
            data-testid="tab-ukg"
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
              selectedTab === 'ukg'
                ? 'bg-slate-700/50 text-white border-b-2 border-[#2EC4B6]'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
            }`}
          >
            <Clock className="w-5 h-5" />
            UKG Integration
          </button>
        </div>

        <div className="p-6">
          {/* File Upload Tab */}
          {selectedTab === 'file' && (
            <div className="space-y-6">
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  dragActive ? 'border-[#2EC4B6] bg-[#2EC4B6]/10' : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <FileUp className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">
                  Drop your configuration file here
                </h3>
                <p className="text-sm text-slate-400 mb-4">
                  Supports Excel (.xlsx), CSV, and JSON formats
                </p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#2EC4B6] text-slate-900 rounded-lg cursor-pointer hover:bg-[#26a69a] transition-colors font-medium">
                  <Upload className="w-4 h-4" />
                  Browse Files
                  <input
                    type="file"
                    className="hidden"
                    accept=".xlsx,.csv,.json"
                    onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
                    data-testid="file-input"
                  />
                </label>
              </div>

              {uploadStatus && (
                <div className={`p-4 rounded-lg ${
                  uploadStatus.type === 'processing' ? 'bg-blue-900/30 border border-blue-700' :
                  uploadStatus.type === 'success' ? 'bg-emerald-900/30 border border-emerald-700' :
                  'bg-red-900/30 border border-red-700'
                }`}>
                  <div className="flex items-center gap-3">
                    {uploadStatus.type === 'processing' && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
                    )}
                    {uploadStatus.type === 'success' && (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    )}
                    <p className={`text-sm ${
                      uploadStatus.type === 'processing' ? 'text-blue-300' :
                      uploadStatus.type === 'success' ? 'text-emerald-300' :
                      'text-red-300'
                    }`}>
                      {uploadStatus.message}
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <h4 className="font-medium text-white mb-2">Supported File Formats</h4>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>- Excel files (.xlsx) - Pay rule exports from UKG or ADP</li>
                  <li>- CSV files - Comma-separated configuration data</li>
                  <li>- JSON files - API exports or structured configuration data</li>
                </ul>
              </div>
            </div>
          )}

          {/* ADP Integration Tab */}
          {selectedTab === 'adp' && (
            <div className="space-y-6">
              <div className="flex items-start gap-6 p-6 bg-slate-900/50 rounded-lg border border-slate-700">
                <div className="w-16 h-16 bg-[#d31145] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">ADP</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">ADP Workforce Now</h3>
                  <p className="text-sm text-slate-400 mb-4">
                    Connect to your ADP Workforce Now account to automatically sync pay rules and configurations.
                  </p>
                  {adpConnected ? (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Connected</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleConnect('adp')}
                      data-testid="connect-adp-btn"
                      className="px-4 py-2 bg-[#d31145] text-white rounded-lg hover:bg-[#b10e3b] transition-colors font-medium"
                    >
                      Connect to ADP
                    </button>
                  )}
                </div>
              </div>

              <div className="border border-slate-700 rounded-lg p-4">
                <h4 className="font-medium text-white mb-3">What gets synced:</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Pay rules and policies
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Overtime configurations
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Break rules
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Employee assignments
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* UKG Integration Tab */}
          {selectedTab === 'ukg' && (
            <div className="space-y-6">
              <div className="flex items-start gap-6 p-6 bg-slate-900/50 rounded-lg border border-slate-700">
                <div className="w-16 h-16 bg-[#00a1e0] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">UKG</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">UKG Pro / UKG Ready</h3>
                  <p className="text-sm text-slate-400 mb-4">
                    Connect to your UKG account to automatically import timekeeping and pay rule configurations.
                  </p>
                  {ukgConnected ? (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Connected</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleConnect('ukg')}
                      data-testid="connect-ukg-btn"
                      className="px-4 py-2 bg-[#00a1e0] text-white rounded-lg hover:bg-[#0088c0] transition-colors font-medium"
                    >
                      Connect to UKG
                    </button>
                  )}
                </div>
              </div>

              <div className="border border-slate-700 rounded-lg p-4">
                <h4 className="font-medium text-white mb-3">What gets synced:</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Work rules and policies
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Overtime rules
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Meal and break rules
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Pay code distributions
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Combination rules
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Employee work assignments
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadConfigurations;
